import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "Components/UI";
import Button from "Components/UI/Button";
import { IDiscipline } from "Data/interfaces/Obra/IObraData";
import { IStage } from "Data/interfaces/Obra/IObraStages";
import { getDisciplineDisplayIcon } from "Utils/ObraUtils";
import {
  getDisciplineDisplayAcronym,
  getDisciplineDisplayName,
} from "../../disciplines.helper";
import { Container, StagesChain } from "./styles";

interface IRow {
  discipline: IDiscipline;
  stages: IStage[];
  onEdit: (discipline: IDiscipline) => void;
  onDelete: (discipline: IDiscipline) => void;
}

const Row: FC<IRow> = ({ discipline, stages, onEdit, onDelete }) => {
  const { t } = useTranslation('editObra');

  const linkedStages = useMemo(() => (
    stages
      .filter((stage) => stage.ConstructionSiteStageDisciplines?.some((csd) => (
        csd.ConstructionSiteDisciplineFk === discipline.ConstructionSiteDisciplinesId
      )))
      .sort((a, b) => a.Order - b.Order)
  ), [stages, discipline]);

  return (
    <Container>
      <div className="colIcon">
        <Icon
          icon={getDisciplineDisplayIcon(discipline)}
          customSize={16}
          className="disciplineIcon"
        />
      </div>
      <div className="colAcronym">
        <span className="rowText">{getDisciplineDisplayAcronym(discipline)}</span>
      </div>
      <div className="colName">
        <span className="rowText">{getDisciplineDisplayName(discipline)}</span>
      </div>
      <div className="colStages">
        {linkedStages.length > 0 ? (
          <StagesChain>
            {linkedStages.map((stage, index) => {
              const isFirst = index === 0;
              const isLast = index === linkedStages.length - 1;
              const className = [
                'stageBadge',
                isFirst && 'isFirst',
                isLast && 'isLast',
              ].filter(Boolean).join(' ');

              return (
                <span key={stage.TopicStageId} className={className}>
                  {!isFirst && <span className="stageBadgeNotch" />}
                  <span className="stageBadgeTag">{stage.Acronym || stage.Name}</span>
                  {!isLast && <span className="stageBadgePoint" />}
                </span>
              );
            })}
          </StagesChain>
        ) : (
          <span className="emptyStagesText">{t('disciplines.noStagesLinked')}</span>
        )}
      </div>
      <div className="colActions">
        <Button type="text" className="actionBtn" onClick={() => onEdit(discipline)}>
          <Icon icon="editar" customSize={12} className="actionIcon" />
        </Button>
        <Button type="text" className="actionBtn" onClick={() => onDelete(discipline)}>
          <Icon icon="excluir" customSize={12} className="actionIcon" />
        </Button>
      </div>
    </Container>
  );
};

export default Row;
